import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  customerGetOrder,
  customerInitiateTestPayment,
  customerCreatePayment,
  customerUpdatePaymentMethod,
  type CustomerOrderResponse,
  type PaymentMethodInfo,
  type PaymentMethod,
  type ErrorResponse,
} from '@vn-propane/shared';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageSelector } from '@/components/ui/language-selector';
import { OrderMap } from '@/components/OrderMap';
import { getImageUrl } from '@/lib/utils';
import {
  Loader2,
  Package,
  Truck,
  CreditCard,
  CheckCircle2,
  MapPin,
  Clock,
  ArrowLeft,
  Banknote,
  CircleHelp,
  ImageIcon,
} from 'lucide-react';

type OrderData = CustomerOrderResponse;

export default function OrderDetailsPage() {
  const { t, i18n } = useTranslation(['orders', 'common', 'errors']);
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isSwitchingPayment, setIsSwitchingPayment] = useState(false);

  useEffect(() => {
    async function loadOrder() {
      if (!orderId) {
        setError(t('errors:orderNotFound'));
        setIsLoading(false);
        return;
      }

      // Check if returning from payment gateway
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatusParam = urlParams.get('payment_status');
      if (paymentStatusParam === 'success') {
        setPaymentSuccess(true);
      } else if (paymentStatusParam === 'failed') {
        setError(t('paymentFailed'));
      }
      // Clean up URL params
      if (paymentStatusParam) {
        window.history.replaceState({}, '', window.location.pathname);
      }

      try {
        const result = await customerGetOrder({ path: { order_id: orderId } });
        if (result.data?.data) {
          setOrder(result.data.data);
        } else if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          if (errorResponse.error?.code === '401') {
            navigate('/login');
            return;
          }
          setError(errorResponse.error?.message || t('errors:failedToLoadOrder'));
        }
      } catch {
        setError(t('errors:unexpectedError'));
      } finally {
        setIsLoading(false);
      }
    }

    loadOrder();
  }, [orderId, t, navigate]);

  // Poll while the delivery is active so the driver pin moves on the map.
  // Cheap and correct until we add a customer WebSocket endpoint.
  useEffect(() => {
    if (!orderId || !order) return;
    const ACTIVE = ['assigned', 'dispatched', 'in_progress', 'arrived'];
    if (!ACTIVE.includes(order.status.toLowerCase())) return;

    const interval = setInterval(async () => {
      try {
        const result = await customerGetOrder({ path: { order_id: orderId } });
        if (result.data?.data) {
          setOrder(result.data.data);
        }
      } catch {
        // Transient — swallow, next tick will retry
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [orderId, order]);

  const handleTestPayment = async (simulateSuccess: boolean = true) => {
    if (!orderId) return;

    setIsProcessingPayment(true);
    setError(null);

    try {
      const result = await customerInitiateTestPayment({
        path: { order_id: orderId },
        body: { simulate_success: simulateSuccess },
      });

      if (result.data?.data) {
        if (result.data.data.status === 'completed') {
          setPaymentSuccess(true);
          // Reload order to get updated status
          const orderResult = await customerGetOrder({ path: { order_id: orderId } });
          if (orderResult.data?.data) {
            setOrder(orderResult.data.data);
          }
        } else {
          setError(t('paymentFailed'));
        }
      } else if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('paymentFailed'));
      }
    } catch {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleMomoPayment = async () => {
    if (!orderId) return;

    setIsProcessingPayment(true);
    setError(null);

    try {
      // Build redirect URL back to this order page
      const redirectUrl = `${window.location.origin}/orders/${orderId}`;

      const result = await customerCreatePayment({
        path: { order_id: orderId },
        body: {
          provider: 'momopay',
          redirectUrl,
        },
      });

      if (result.data?.data) {
        const { payUrl, deeplink } = result.data.data;

        // On mobile, try deeplink first, then fall back to payUrl
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile && deeplink) {
          window.location.href = deeplink;
          // Fallback to payUrl after a short delay if deeplink doesn't work
          setTimeout(() => {
            if (payUrl) {
              window.location.href = payUrl;
            }
          }, 2000);
        } else if (payUrl) {
          window.location.href = payUrl;
        } else {
          setError(t('paymentFailed'));
        }
      } else if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('paymentFailed'));
      }
    } catch {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleSwitchPaymentMethod = async (method: PaymentMethod) => {
    if (!orderId || !order || order.payment_method === method) return;

    setIsSwitchingPayment(true);
    setError(null);

    try {
      const result = await customerUpdatePaymentMethod({
        path: { order_id: orderId },
        body: { payment_method: method },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('errors:unexpectedError'));
      } else {
        // Reload order
        const orderResult = await customerGetOrder({ path: { order_id: orderId } });
        if (orderResult.data?.data) {
          setOrder(orderResult.data.data);
        }
      }
    } catch {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsSwitchingPayment(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-carbon-200 text-carbon-800';
      case 'assigned':
      case 'dispatched':
        return 'bg-info/10 text-info';
      case 'in_progress':
        return 'bg-warning text-carbon-950';
      case 'arrived':
        return 'bg-propane-100 text-propane-700';
      case 'delivered':
      case 'completed':
        return 'bg-success/10 text-success';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-carbon-200 text-carbon-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return t('status.pending');
      case 'assigned':
        return t('status.assigned');
      case 'dispatched':
        return t('status.dispatched');
      case 'in_progress':
        return t('status.in_progress');
      case 'arrived':
        return t('status.arrived');
      case 'delivered':
        return t('status.delivered');
      case 'completed':
        return t('status.completed');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 relative">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <a
            href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t('common:navigation.help')}
          >
            <CircleHelp className="h-5 w-5" />
          </a>
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md p-8 bg-background rounded-lg border shadow-sm">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-muted/40 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <a
          href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={t('common:navigation.help')}
        >
          <CircleHelp className="h-5 w-5" />
        </a>
        <LanguageSelector />
      </div>

      <div className="max-w-2xl mx-auto p-4 pt-16">
        {/* Back to Orders */}
        <Link
          to="/orders"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToOrders')}
        </Link>

        {/* Header */}
        <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
          {/* Gas Station Logo */}
          {(order as { gas_station_logo_url?: string }).gas_station_logo_url && (
            <div className="flex justify-center mb-4">
              <img
                src={getImageUrl((order as { gas_station_logo_url?: string }).gas_station_logo_url)!}
                alt={order.gas_station_name || t('gasStationLogo')}
                className="h-16 max-w-[200px] object-contain"
              />
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-extrabold tracking-tight">{t('orderDetails')}</h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}
            >
              {getStatusLabel(order.status)}
            </span>
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{formatDate(order.created_at)}</span>
            </div>
            {order.organization_name && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{order.organization_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Live tracking map — renders when the order has a geocoded destination. */}
        {order.customer_latitude != null && order.customer_longitude != null && (
          <div className="bg-background rounded-sm border shadow-sm p-3 mb-4">
            <OrderMap
              destination={[order.customer_latitude, order.customer_longitude]}
              station={
                order.gas_station_latitude != null && order.gas_station_longitude != null
                  ? [order.gas_station_latitude, order.gas_station_longitude]
                  : null
              }
              driver={
                order.driver_latitude != null && order.driver_longitude != null
                  ? [order.driver_latitude, order.driver_longitude]
                  : null
              }
              height={260}
            />
          </div>
        )}

        {/* Driver Info */}
        {order.driver_name && (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {t('driverInfo')}
            </h2>
            <div className="text-sm">
              <p className="font-medium">{order.driver_name}</p>
              {order.gas_station_name && (
                <p className="text-muted-foreground">{order.gas_station_name}</p>
              )}
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t('orderItems')}
          </h2>

          <div className="space-y-3">
            {order.items.map((item, index) => {
              const imageSrc = getImageUrl(item.product_image_url);
              return (
                <div
                  key={index}
                  className="flex justify-between items-center py-2 border-b last:border-0 gap-3"
                >
                  <div className="w-10 h-10 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.quantity} x {formatCurrency(item.unit_price)}
                    </p>
                  </div>
                  <p className="font-medium">{formatCurrency(item.subtotal)}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t flex justify-between items-center">
            <span className="text-lg font-semibold">{t('total')}</span>
            <span className="text-xl font-extrabold tabular-nums text-primary">
              {formatCurrency(order.total_amount)}
            </span>
          </div>
        </div>

        {/* Payment Section */}
        {order.status === 'completed' ? (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <div className="flex items-center gap-3 text-green-600">
              <CheckCircle2 className="h-6 w-6" />
              <div>
                <h2 className="text-lg font-semibold">{t('alreadyPaid')}</h2>
                <p className="text-sm text-muted-foreground">{t('orderCompleted')}</p>
              </div>
            </div>
          </div>
        ) : order.status !== 'cancelled' ? (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {t('paymentMethod')}
            </h2>

            {/* Payment method toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => handleSwitchPaymentMethod('online')}
                disabled={isSwitchingPayment}
                className={`flex-1 p-3 rounded-lg border text-center transition-colors flex items-center justify-center gap-2 ${
                  order.payment_method === 'online'
                    ? 'border-primary bg-primary/5 font-medium'
                    : 'border-border hover:bg-muted/50 text-muted-foreground'
                }`}
              >
                <CreditCard className="h-4 w-4" />
                {t('onlinePayment')}
              </button>
              <button
                onClick={() => handleSwitchPaymentMethod('cash')}
                disabled={isSwitchingPayment}
                className={`flex-1 p-3 rounded-lg border text-center transition-colors flex items-center justify-center gap-2 ${
                  order.payment_method === 'cash'
                    ? 'border-primary bg-primary/5 font-medium'
                    : 'border-border hover:bg-muted/50 text-muted-foreground'
                }`}
              >
                <Banknote className="h-4 w-4" />
                {t('cashOnDelivery')}
              </button>
            </div>

            {isSwitchingPayment && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common:loading')}
              </div>
            )}

            {order.payment_method === 'cash' ? (
              <div className="flex items-center gap-3 text-carbon-950 p-3 bg-warning rounded-sm">
                <Banknote className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm font-medium">{t('cashPaymentPending')}</p>
              </div>
            ) : (
              <>
                {paymentSuccess && (
                  <Alert className="mb-4 border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      {t('paymentSuccessful')}
                    </AlertDescription>
                  </Alert>
                )}

                {order.available_payment_methods.length > 0 ? (
                  <div className="space-y-3">
                    {order.available_payment_methods.map((method: PaymentMethodInfo) => (
                      <div
                        key={method.provider}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {method.provider === 'momopay' && (
                            <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center text-pink-600 font-bold text-sm">
                              M
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{method.name}</p>
                            <p className="text-sm text-muted-foreground">{method.description}</p>
                          </div>
                        </div>
                        {method.provider === 'test' ? (
                          <Button
                            onClick={() => handleTestPayment(true)}
                            disabled={isProcessingPayment || paymentSuccess}
                          >
                            {isProcessingPayment ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {t('processing')}
                              </>
                            ) : paymentSuccess ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                {t('paid')}
                              </>
                            ) : (
                              t('payNow')
                            )}
                          </Button>
                        ) : method.provider === 'momopay' ? (
                          <Button
                            onClick={handleMomoPayment}
                            disabled={isProcessingPayment || paymentSuccess}
                            className="bg-pink-600 hover:bg-pink-700"
                          >
                            {isProcessingPayment ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {t('processing')}
                              </>
                            ) : paymentSuccess ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                {t('paid')}
                              </>
                            ) : (
                              t('payNow')
                            )}
                          </Button>
                        ) : (
                          <Button variant="outline" disabled>
                            {t('comingSoon')}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('noPaymentMethods')}</p>
                )}
              </>
            )}
          </div>
        ) : null}

        {/* Notes */}
        {order.notes && (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('notes')}</h2>
            <p className="text-sm text-muted-foreground">{order.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
